import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useMerchantId } from '@/hooks/useMerchantId';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Link } from 'react-router-dom';
import { Link2, Eye, RefreshCw, ShoppingBag, Briefcase } from 'lucide-react';

const statusColors: Record<string, string> = {
  Ulanmagan: 'bg-muted text-muted-foreground',
  Ulangan: 'bg-success/10 text-success',
  Xato: 'bg-destructive/10 text-destructive',
};

const dateLocale = (lng: string) => lng === 'ru' ? 'ru-RU' : lng === 'en' ? 'en-US' : 'uz';

export default function ModulesPage() {
  const { t, i18n } = useTranslation();
  const { merchantId } = useMerchantId();
  const { data: modulesData, isLoading } = useQuery({ queryKey: ['/modules'] });
  const modules = modulesData?.data ?? [];
  const { data: connectionsData } = useQuery({ queryKey: ['/module-connections'], enabled: !!merchantId });
  const moduleConnections = connectionsData?.data ?? [];
  const { data: syncStatsData } = useQuery({ queryKey: ['/module-sync-stats'], enabled: !!merchantId });
  const moduleSyncStats = syncStatsData?.data ?? [];
  const [connectModal, setConnectModal] = useState<string | null>(null);

  const getStatus = (code: string) => {
    const conn = moduleConnections.find(mc => mc.module_code === code);
    return conn?.status ?? 'Ulanmagan';
  };

  const getLastSync = (code: string) => {
    const conn = moduleConnections.find(mc => mc.module_code === code);
    return conn?.last_sync_at ? new Date(conn.last_sync_at).toLocaleString(dateLocale(i18n.language)) : '-';
  };

  const getSyncStats = (code: string) => {
    return moduleSyncStats.filter(ms => ms.module_code === code);
  };

  const commerceModules = modules.filter(m => m.grp === 'commerce');
  const businessModules = modules.filter(m => m.grp === 'business_ops');

  const statusLabels: Record<string, string> = {
    Ulanmagan: t('cat_modules.statusDisconnected'),
    Ulangan: t('cat_modules.statusConnected'),
    Xato: t('cat_modules.statusError'),
  };

  const ModuleCard = ({ mod }: { mod: typeof modules[0] }) => {
    const status = getStatus(mod.code);
    const isConnected = status === 'Ulangan';
    const stats = getSyncStats(mod.code);

    return (
      <div className="bg-card border border-border rounded-xl p-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="text-sm font-medium text-foreground">{mod.name}</p>
          </div>
          <Badge className={`text-xs border-0 ${statusColors[status] || ''}`}>{statusLabels[status] ?? status}</Badge>
        </div>

        {isConnected && (
          <div className="mb-3 space-y-1">
            <p className="text-xs text-muted-foreground">{t('cat_modules.lastSync', { date: getLastSync(mod.code) })}</p>
            {stats.map(s => (
              <p key={s.id} className="text-xs text-muted-foreground">{s.object_type}: {s.object_count}</p>
            ))}
          </div>
        )}

        <div className="flex gap-2">
          {!isConnected ? (
            <Button size="sm" variant="outline" onClick={() => setConnectModal(mod.code)} className="text-xs">
              <Link2 size={12} className="mr-1" /> {t('cat_modules.connect')}
            </Button>
          ) : (
            <Button size="sm" variant="outline" className="text-xs">
              <RefreshCw size={12} className="mr-1" /> {t('cat_modules.sync')}
            </Button>
          )}
          <Button size="sm" variant="ghost" asChild className="text-xs">
            <Link to={mod.view_url}>
              <Eye size={12} className="mr-1" /> {t('cat_modules.view')}
            </Link>
          </Button>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return <div className="p-6 text-center text-muted-foreground">{t('cat_common.loading')}</div>;
  }

  return (
    <div className="p-6 max-w-6xl mx-auto animate-fade-in">
      <h1 className="text-xl font-semibold text-foreground mb-1">{t('cat_modules.title')}</h1>
      <p className="text-sm text-muted-foreground mb-6">{t('cat_modules.subtitle')}</p>

      <div className="mb-8">
        <div className="flex items-center gap-2 mb-3">
          <ShoppingBag size={16} className="text-primary" />
          <h2 className="text-sm font-semibold text-foreground">Commerce</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {commerceModules.map(mod => <ModuleCard key={mod.id} mod={mod} />)}
        </div>
      </div>

      <div>
        <div className="flex items-center gap-2 mb-3">
          <Briefcase size={16} className="text-primary" />
          <h2 className="text-sm font-semibold text-foreground">Business Ops</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {businessModules.map(mod => <ModuleCard key={mod.id} mod={mod} />)}
        </div>
      </div>

      <Dialog open={!!connectModal} onOpenChange={() => setConnectModal(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('cat_modules.connectTitle')}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              {t('cat_modules.connectText', { name: modules.find(m => m.code === connectModal)?.name })}
            </p>
            <div className="flex gap-3">
              <Button onClick={() => setConnectModal(null)} className="flex-1">{t('cat_modules.confirm')}</Button>
              <Button variant="outline" onClick={() => setConnectModal(null)} className="flex-1">{t('cat_common.cancel')}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
